import React, { useState, useEffect } from 'react';
import { customerAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import { INDIA_STATES, getCitiesForState } from '../../../constants/indianLocations';
import {
  Button,
  Modal,
  Input,
  Select,
  ErrorMessage,
  SuccessMessage,
  Badge,
  Pagination,
} from '../../UI';
import SearchInput from '../../UI/SearchInput';
import { SearchStatus, SearchResultsSkeleton } from '../../UI/SearchLoader';
import { Users, Eye, Edit, Trash2, Download, Phone, Mail } from 'lucide-react';

const CustomersPage = () => {
  const { data: customersData, loading, refetch } = useApi(customerAPI.getCustomers, []);
  const { data: statsData, loading: statsLoading } = useApi(customerAPI.getStats, []);
  
  const { loading: editLoading, execute: executeEdit } = useAsyncAction();
  const { loading: deleteLoading, execute: executeDelete } = useAsyncAction();
  const { loading: exportLoading, execute: executeExport } = useAsyncAction();
  
  const customers = customersData?.customers || [];
  const stats = statsData || {};
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  
  const [form, setForm] = useState({
    name: '', phone: '', email: '', gender: '', maritalStatus: '',
    dateOfBirth: '', dateOfAnniversary: '', location: '', area: '',
    address: { street: '', city: '', state: '' },
    pincode: '', customerDesignation: '', source: '', status: '', notes: '',
    interested: '', followUpDate: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search) {
        setSearchLoading(true);
        refetch({ search }).finally(() => setSearchLoading(false));
      } else {
        refetch();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const handleExport = async () => {
    const result = await executeExport(customerAPI.exportToExcel);
    if (result.success && result.data?.url) {
      window.open(result.data.url, '_blank');
      setSuccess('Export started');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const result = await executeEdit(customerAPI.updateCustomer, selected._id, form);
    if (result.success) {
      setSuccess('Customer updated');
      setIsEditModalOpen(false);
      setTimeout(() => refetch(), 100);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Update failed');
    }
  };

  const handleDelete = async () => {
    const result = await executeDelete(customerAPI.deleteCustomer, selected._id);
    if (result.success) {
      setSuccess('Customer deleted');
      setIsDeleteModalOpen(false);
      refetch();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Delete failed');
    }
  };

  const openEditModal = (c) => {
    setSelected(c);
    setForm({
      name: c.name || '', phone: c.phone || '', email: c.email || '',
      gender: c.gender || '', maritalStatus: c.maritalStatus || '',
      dateOfBirth: c.dateOfBirth?.split('T')[0] || '',
      dateOfAnniversary: c.dateOfAnniversary?.split('T')[0] || '',
      location: c.location || '', area: c.area || '',
      address: { street: c.address?.street || '', city: c.address?.city || '', state: c.address?.state || '' },
      pincode: c.pincode || '', customerDesignation: c.customerDesignation || '',
      source: c.source || '', status: c.status || '', notes: c.notes || '',
      interested: c.interested || '', followUpDate: c.followUpDate?.split('T')[0] || ''
    });
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Customers</h2>
          <p className="text-gray-500">View and manage all customers</p>
        </div>
        <Button onClick={handleExport} loading={exportLoading} variant="outline" className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          Export Excel
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Customers</p>
          <p className="text-2xl font-bold text-gray-900">{statsLoading ? '...' : stats.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">This Month</p>
          <p className="text-2xl font-bold text-green-600">{statsLoading ? '...' : stats.thisMonth || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Marketing</p>
          <p className="text-2xl font-bold text-blue-600">{statsLoading ? '...' : stats.bySource?.Marketing || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Instore</p>
          <p className="text-2xl font-bold text-purple-600">{statsLoading ? '...' : stats.bySource?.Instore || 0}</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <SearchInput
          placeholder="Search customers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          isLoading={searchLoading}
          className="max-w-md"
        />
        <SearchStatus isSearching={searchLoading} resultCount={customers.length} searchTerm={search} />
      </div>

      {/* Table */}
      {loading ? (
        <SearchResultsSkeleton rows={5} />
      ) : customers.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customers.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center text-white font-medium">
                          {c.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{c.name}</p>
                          {c.email ? (
                            <a href={`mailto:${c.email}`} className="text-sm text-blue-600 hover:text-blue-700 hover:underline">
                              {c.email}
                            </a>
                          ) : (
                            <p className="text-sm text-gray-500">No email</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <a href={`tel:${c.phone}`} className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline">
                        {c.phone}
                      </a>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{c.location} - {c.area}</td>
                    <td className="px-6 py-4">
                      <Badge variant={c.source === 'Marketing' ? 'primary' : c.source === 'Instore' ? 'success' : 'warning'}>
                        {c.source}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{c.addedBy?.name || 'N/A'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setSelected(c); setIsViewModalOpen(true); }} className="p-2 rounded-lg hover:bg-gray-100" title="View Details">
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        <a href={`tel:${c.phone}`} className="p-2 rounded-lg hover:bg-gray-100" title="Call Customer">
                          <Phone className="w-4 h-4 text-green-600" />
                        </a>
                        <button onClick={() => openEditModal(c)} className="p-2 rounded-lg hover:bg-gray-100" title="Edit Customer">
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <button onClick={() => { setSelected(c); setIsDeleteModalOpen(true); }} className="p-2 rounded-lg hover:bg-gray-100" title="Delete Customer">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {customersData?.pagination?.pages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <Pagination
                currentPage={customersData.pagination.page}
                totalPages={customersData.pagination.pages}
                onPageChange={(page) => refetch({ page })}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-1">No customers found</h3>
          <p className="text-gray-500">{search ? `No results for "${search}"` : 'No customers added yet'}</p>
        </div>
      )}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Customer Details" size="lg">
        {selected && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center text-white font-bold text-2xl">
                {selected.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selected.name}</h3>
                {selected.businessName && <p className="text-gray-500">{selected.businessName}</p>}
                <div className="flex gap-2 mt-1">
                  <Badge variant={selected.source === 'Marketing' ? 'primary' : selected.source === 'Instore' ? 'success' : 'warning'}>{selected.source}</Badge>
                  <Badge variant={selected.status === 'active' ? 'success' : selected.status === 'converted' ? 'primary' : 'secondary'}>{selected.status}</Badge>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-sm font-semibold text-green-600 uppercase tracking-wide mb-3">Contact Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Phone</p>
                  <a href={`tel:${selected.phone}`} className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
                    {selected.phone}
                  </a>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Email</p>
                  {selected.email ? (
                    <a href={`mailto:${selected.email}`} className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
                      {selected.email}
                    </a>
                  ) : (
                    <p className="font-medium text-gray-900">N/A</p>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Info */}
            <div>
              <h4 className="text-sm font-semibold text-green-600 uppercase tracking-wide mb-3">Personal Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Gender</p>
                  <p className="font-medium text-gray-900 capitalize">{selected.gender || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Marital Status</p>
                  <p className="font-medium text-gray-900 capitalize">{selected.maritalStatus || 'N/A'}</p>
                </div>
                {selected.dateOfBirth && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Date of Birth</p>
                    <p className="font-medium text-gray-900">{new Date(selected.dateOfBirth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                )}
                {selected.dateOfAnniversary && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Anniversary</p>
                    <p className="font-medium text-gray-900">{new Date(selected.dateOfAnniversary).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Location & Address */}
            <div>
              <h4 className="text-sm font-semibold text-green-600 uppercase tracking-wide mb-3">Location & Address</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="font-medium text-gray-900">{selected.location || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Area</p>
                  <p className="font-medium text-gray-900">{selected.area || 'N/A'}</p>
                </div>
                {selected.address?.street && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Street</p>
                    <p className="font-medium text-gray-900">{selected.address.street}</p>
                  </div>
                )}
                {selected.address?.city && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">City</p>
                    <p className="font-medium text-gray-900">{selected.address.city}</p>
                  </div>
                )}
                {selected.address?.state && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">State</p>
                    <p className="font-medium text-gray-900">{selected.address.state}</p>
                  </div>
                )}
                {selected.pincode && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Pincode</p>
                    <p className="font-medium text-gray-900">{selected.pincode}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Marketing Details */}
            <div>
              <h4 className="text-sm font-semibold text-green-600 uppercase tracking-wide mb-3">Marketing Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Source</p>
                  <Badge variant={selected.source === 'Marketing' ? 'primary' : selected.source === 'Instore' ? 'success' : 'warning'}>{selected.source}</Badge>
                </div>
                {selected.customerDesignation && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Designation</p>
                    <p className="font-medium text-gray-900">{selected.customerDesignation}</p>
                  </div>
                )}
                {selected.otherStoreName && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Other Store</p>
                    <p className="font-medium text-gray-900">{selected.otherStoreName}</p>
                  </div>
                )}
                {selected.otherStoreEndDate && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Store End Date</p>
                    <p className="font-medium text-gray-900">{new Date(selected.otherStoreEndDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                )}
                {selected.visitDate && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Visit Date</p>
                    <p className="font-medium text-gray-900">{new Date(selected.visitDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                )}
                {selected.branchName && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Branch</p>
                    <p className="font-medium text-gray-900">{selected.branchName}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Added By</p>
                  <p className="font-medium text-gray-900">{selected.addedBy?.name || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Added On</p>
                  <p className="font-medium text-gray-900">{new Date(selected.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
                {selected.interested && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Interested</p>
                    <Badge variant={selected.interested === 'yes' ? 'success' : selected.interested === 'no' ? 'danger' : 'warning'}>
                      {selected.interested === 'yes' ? 'Yes - Interested' : selected.interested === 'no' ? 'No - Not Interested' : 'Maybe - Follow Up'}
                    </Badge>
                  </div>
                )}
                {selected.followUpDate && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Follow-up Date</p>
                    <p className="font-medium text-gray-900">
                      {new Date(selected.followUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {selected.notes && (
              <div>
                <h4 className="text-sm font-semibold text-green-600 uppercase tracking-wide mb-3">Notes</h4>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-900">{selected.notes}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Customer" size="lg">
        <form onSubmit={handleEdit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Input label="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Select label="Gender *" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} required>
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Select>
            <Select label="Marital Status *" value={form.maritalStatus} onChange={(e) => setForm({ ...form, maritalStatus: e.target.value })} required>
              <option value="">Select Status</option>
              <option value="unmarried">Unmarried</option>
              <option value="married">Married</option>
            </Select>
            {form.maritalStatus === 'married' && (
              <Input label="Anniversary Date" type="date" value={form.dateOfAnniversary} onChange={(e) => setForm({ ...form, dateOfAnniversary: e.target.value })} />
            )}
            <Input label="Date of Birth" type="date" value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
            <Select label="State *" value={form.address.state} onChange={(e) => { const s = e.target.value; setForm({ ...form, address: { ...form.address, state: s, city: '' }, location: '' }); }} required>
              <option value="">Select State</option>
              {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Select label="City / Location *" value={form.location} onChange={(e) => { const c = e.target.value; setForm({ ...form, location: c, address: { ...form.address, city: c } }); }} required disabled={!form.address.state}>
              <option value="">{form.address.state ? 'Select City' : 'Select State first'}</option>
              {getCitiesForState(form.address.state).map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Input label="Area / Locality" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. Banjara Hills" />
            <Input label="Street / Plot / Door No" value={form.address.street} onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })} />
            <Input label="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
            <Input label="Customer Designation" value={form.customerDesignation} onChange={(e) => setForm({ ...form, customerDesignation: e.target.value })} />
            <Select label="Source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option value="">Select Source</option>
              <option value="Marketing">Marketing</option>
              <option value="Instore">Instore</option>
              <option value="Online">Online</option>
              <option value="Referral">Referral</option>
            </Select>
            <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="">Select Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="prospect">Prospect</option>
              <option value="converted">Converted</option>
            </Select>
            <Select label="Interested?" value={form.interested} onChange={(e) => setForm({ ...form, interested: e.target.value })}>
              <option value="">Select</option>
              <option value="yes">Yes - Interested</option>
              <option value="no">No - Not Interested</option>
              <option value="maybe">Maybe - Follow Up</option>
            </Select>
            <Input label="Follow-up Date" type="date" value={form.followUpDate} onChange={(e) => setForm({ ...form, followUpDate: e.target.value })} />
          </div>
          <Input label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={editLoading}>Update</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete Customer" size="sm">
        <div className="text-center">
          <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Delete <strong>{selected?.name}</strong>?</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteLoading}>Delete</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CustomersPage;
